import { motion } from "framer-motion";
import { Text, Button } from "@core/index";
import { NewEssayEvaluationResult } from "@packages/api/madlenAPI.schemas";
import {
  ArrowRight,
  CheckCircle,
  AlertCircle,
  FileText,
  Award,
  Layers,
  Quote,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  BarChart,
} from "lucide-react";
import { useState, useMemo, useRef } from "react";
import EssayGraderPdfDocument from "./EssayGraderPdf";
import PdfDownloadButton from "../../../tools/components/pdf/PdfDownloadButton";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Score visualization components
interface ScoreCircleProps {
  score: string;
  size?: "sm" | "md" | "lg";
  showText?: boolean;
}

const ScoreCircle: React.FC<ScoreCircleProps> = ({
  score,
  size = "md",
  showText = true,
}) => {
  // Parse the score (expected format: "X/Y")
  const parts = score.split("/");
  const scoreValue = parts.length === 2 ? parseFloat(parts[0]) : 0;
  const maxScore = parts.length === 2 ? parseFloat(parts[1]) : 10;

  // Calculate percentage
  const percentage = Math.min(Math.max((scoreValue / maxScore) * 100, 0), 100);

  // Determine color based on percentage
  const getColor = () => {
    if (percentage >= 80) return "text-green-500 dark:text-green-400";
    if (percentage >= 60) return "text-blue-500 dark:text-blue-400";
    if (percentage >= 40) return "text-yellow-500 dark:text-yellow-400";
    return "text-red-500 dark:text-red-400";
  };

  // Determine background color
  const getBgColor = () => {
    if (percentage >= 80) return "text-green-100 dark:text-green-900";
    if (percentage >= 60) return "text-blue-100 dark:text-blue-900";
    if (percentage >= 40) return "text-yellow-100 dark:text-yellow-900";
    return "text-red-100 dark:text-red-900";
  };

  // Size classes
  const sizeClasses = {
    sm: {
      container: "w-8 h-8",
      text: "text-xs",
      strokeWidth: 2.5,
    },
    md: {
      container: "w-12 h-12",
      text: "text-sm",
      strokeWidth: 2,
    },
    lg: {
      container: "w-16 h-16",
      text: "text-base",
      strokeWidth: 1.5,
    },
  };

  // Calculate stroke-dasharray and stroke-dashoffset
  const radius = size === "sm" ? 15 : size === "md" ? 22 : 30;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className={`relative ${sizeClasses[size].container} flex items-center justify-center`}
    >
      {/* Background circle */}
      <svg className="w-full h-full" viewBox="0 0 64 64">
        <circle
          className={getBgColor()}
          strokeWidth={sizeClasses[size].strokeWidth}
          fill="none"
          cx="32"
          cy="32"
          r={radius}
        />
        {/* Progress circle */}
        <circle
          className={`${getColor()} transition-all duration-1000 ease-out`}
          strokeWidth={sizeClasses[size].strokeWidth}
          strokeLinecap="round"
          stroke="currentColor"
          fill="none"
          cx="32"
          cy="32"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 32 32)"
        />
      </svg>

      {/* Text in center */}
      {showText && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Text
            variant="span"
            className={`font-semibold ${sizeClasses[size].text} ${getColor()}`}
          >
            {scoreValue}/{maxScore}
          </Text>
        </div>
      )}
    </div>
  );
};

// RadarChart component for overall criteria performance visualization
interface RadarChartProps {
  criteria: {
    name: string;
    score: string;
  }[];
  onCriterionClick?: (criterionIndex: number) => void;
}

const RadarChart: React.FC<RadarChartProps> = ({
  criteria,
  onCriterionClick,
}) => {
  const { t } = useTranslation();
  const [activePointIndex, setActivePointIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Convert scores to percentages for radar visualization
  const scores = useMemo(() => {
    return criteria.map((criterion) => {
      const parts = criterion.score ? criterion.score.split("/") : ["0", "1"];
      const scoreValue = parts.length === 2 ? parseFloat(parts[0]) : 0;
      const maxScore = parts.length === 2 ? parseFloat(parts[1]) : 10;
      return {
        name: criterion.name || "",
        score: criterion.score || "0/0",
        percentage: Math.min(Math.max((scoreValue / maxScore) * 100, 0), 100),
        originalScore: {
          value: scoreValue,
          max: maxScore,
        },
      };
    });
  }, [criteria]);

  // Radar chart geometry calculations
  const centerX = 150;
  const centerY = 150;
  const radius = 100;
  const totalCriteria = scores.length;

  // Need at least 3 criteria for a meaningful radar chart
  if (totalCriteria < 3) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <Text
          variant="h5"
          className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300"
        >
          {t("assistant.essayGrader.performanceChart")}
        </Text>
        <div className="h-48 w-full flex items-center justify-center">
          <Text
            variant="p"
            className="text-sm text-gray-500 dark:text-gray-400 text-center"
          >
            {t("assistant.essayGrader.notEnoughCriteria")} <br />
            {t("assistant.essayGrader.minimumCriteriaRequired")}
          </Text>
        </div>
      </div>
    );
  }

  // Calculate points for each criteria
  const getPointsData = () => {
    // Returns array of coordinate objects for the polygon
    return scores.map((score, i) => {
      const angle = (Math.PI * 2 * i) / totalCriteria;
      const scoreRadius = (radius * score.percentage) / 100;
      const x = centerX + scoreRadius * Math.sin(angle);
      const y = centerY - scoreRadius * Math.cos(angle);
      return { x, y, score };
    });
  };

  // Handle point hover with debouncing for smoother experience
  const handlePointHover = (index: number, x: number, y: number) => {
    setActivePointIndex(index);

    // Adjust tooltip position based on quadrant to prevent going off-screen
    const angle = (Math.PI * 2 * index) / totalCriteria;
    let tooltipX = x;
    let tooltipY = y;

    // Adjust based on which quadrant the point is in
    if (angle <= Math.PI / 2) {
      // Top right
      tooltipX = x + 10;
      tooltipY = y - 70;
    } else if (angle <= Math.PI) {
      // Bottom right
      tooltipX = x + 10;
      tooltipY = y + 10;
    } else if (angle <= (Math.PI * 3) / 2) {
      // Bottom left
      tooltipX = x - 160;
      tooltipY = y + 10;
    } else {
      // Top left
      tooltipX = x - 160;
      tooltipY = y - 70;
    }

    setTooltipPosition({ x: tooltipX, y: tooltipY });
  };

  const handlePointLeave = () => {
    setActivePointIndex(null);
  };

  // Get the polygon points string
  const pointsData = getPointsData();
  const polygonPoints = pointsData
    .map((point) => `${point.x},${point.y}`)
    .join(" ");

  // Get axis line endpoints
  const getAxisLines = () => {
    return scores.map((_, i) => {
      const angle = (Math.PI * 2 * i) / totalCriteria;
      const x = centerX + radius * Math.sin(angle);
      const y = centerY - radius * Math.cos(angle);
      return { x1: centerX, y1: centerY, x2: x, y2: y };
    });
  };

  // Get label positions
  const getLabelPositions = () => {
    return scores.map((score, i) => {
      const angle = (Math.PI * 2 * i) / totalCriteria;
      // Position labels a bit outside the radar
      const x = centerX + (radius + 20) * Math.sin(angle);
      const y = centerY - (radius + 20) * Math.cos(angle);
      // Adjust text alignment based on position
      let textAnchor = "middle";
      if (x < centerX - 10) textAnchor = "end";
      if (x > centerX + 10) textAnchor = "start";
      return { x, y, text: score.name, textAnchor };
    });
  };

  // Create concentric circles for the grid
  const gridCircles = [25, 50, 75, 100].map((percentage) => ({
    r: (radius * percentage) / 100,
    percentage,
  }));

  // Get color based on percentage
  const getPointColor = (percentage: number) => {
    if (percentage >= 70) return "#10B981"; // green-500 for high scores
    if (percentage >= 40) return "#F59E0B"; // amber-500 for medium scores
    return "#EF4444"; // red-500 for low scores
  };

  // Get hover color (slightly brighter)
  const getPointHoverColor = (percentage: number) => {
    if (percentage >= 70) return "#34D399"; // green-400 for high scores
    if (percentage >= 40) return "#FBBF24"; // amber-400 for medium scores
    return "#F87171"; // red-400 for low scores
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <Text
        variant="h5"
        className="text-sm font-medium mb-3 text-gray-700 dark:text-gray-300"
      >
        {t("assistant.essayGrader.performanceChart")}
      </Text>
      <div className="flex justify-center">
        <svg
          width="300"
          height="300"
          viewBox="0 0 300 300"
          className="overflow-visible"
          role="img"
          aria-label={t("assistant.essayGrader.radarChartTitle")}
        >
          {/* Grid circles */}
          {gridCircles.map((circle, i) => (
            <circle
              key={i}
              cx={centerX}
              cy={centerY}
              r={circle.r}
              stroke="#CBD5E1"
              strokeWidth="1"
              fill="none"
              className="dark:stroke-gray-600"
            />
          ))}

          {/* Grid percentage labels */}
          {gridCircles.map((circle, i) => (
            <text
              key={i}
              x={centerX}
              y={centerY - circle.r - 2}
              fontSize="8"
              textAnchor="middle"
              className="fill-gray-500 dark:fill-gray-400"
            >
              {circle.percentage}%
            </text>
          ))}

          {/* Axis lines */}
          {getAxisLines().map((line, i) => (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke="#CBD5E1"
              strokeWidth="1"
              className="dark:stroke-gray-600"
            />
          ))}

          {/* Data polygon */}
          <polygon
            points={polygonPoints}
            fill="rgba(99, 102, 241, 0.2)"
            stroke="#6366F1"
            strokeWidth="2"
            className="dark:fill-indigo-900/30 dark:stroke-indigo-400 transition-all duration-300 ease-in-out"
          />

          {/* Data points with interaction */}
          {pointsData.map((point, i) => {
            const isActive = activePointIndex === i;
            const pointColor = getPointColor(point.score.percentage);
            const hoverColor = getPointHoverColor(point.score.percentage);

            return (
              <g key={i} className="cursor-pointer group">
                {/* Larger transparent hit area for better touch/mouse interaction */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="15"
                  fill="transparent"
                  onMouseEnter={() => handlePointHover(i, point.x, point.y)}
                  onMouseLeave={handlePointLeave}
                  onClick={() => onCriterionClick && onCriterionClick(i)}
                  className="transition-all duration-200"
                  style={{ touchAction: "none" }}
                />
                {/* Actual visible point */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isActive ? "6" : "4"}
                  fill={isActive ? hoverColor : pointColor}
                  stroke="white"
                  strokeWidth="1.5"
                  className="dark:stroke-gray-900 transition-all duration-200"
                  pointerEvents="none"
                />

                {/* Subtle glow effect for better visibility */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isActive ? "10" : "0"}
                  fill="transparent"
                  stroke={hoverColor}
                  strokeWidth="1"
                  opacity={isActive ? "0.5" : "0"}
                  className="transition-all duration-200"
                  pointerEvents="none"
                />
              </g>
            );
          })}

          {/* Labels */}
          {getLabelPositions().map((label, i) => {
            const isActive = activePointIndex === i;

            return (
              <text
                key={i}
                x={label.x}
                y={label.y}
                textAnchor={label.textAnchor}
                fontSize="10"
                className={`fill-gray-700 dark:fill-gray-300 transition-all duration-200 ${
                  isActive ? "font-medium" : ""
                }`}
                fontWeight={isActive ? "bold" : "normal"}
              >
                {label.text}
              </text>
            );
          })}

          {/* Tooltip for active point */}
          {activePointIndex !== null && (
            <foreignObject
              x={tooltipPosition.x}
              y={tooltipPosition.y}
              width="250"
              height="100"
              className="overflow-visible pointer-events-none"
            >
              <div className="bg-white dark:bg-gray-800 p-2 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 text-xs">
                <div className="font-semibold">
                  {scores[activePointIndex].name}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t("assistant.essayGrader.score")}
                  </span>
                  <span className="font-medium">
                    {scores[activePointIndex].score}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">
                    {t("assistant.essayGrader.percentage")}
                  </span>
                  <span className="font-medium">
                    {scores[activePointIndex].percentage.toFixed(0)}%
                  </span>
                </div>
              </div>
            </foreignObject>
          )}
        </svg>
      </div>

      {/* Legend with better hover interaction */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        {scores.map((score, i) => {
          const isActive = activePointIndex === i;
          return (
            <div
              key={i}
              className={`flex items-center p-1.5 rounded cursor-pointer transition-colors duration-200 ${
                isActive
                  ? "bg-gray-100 dark:bg-gray-700"
                  : "hover:bg-gray-50 dark:hover:bg-gray-750"
              }`}
              onMouseEnter={() => {
                const pointData = pointsData[i];
                handlePointHover(i, pointData.x, pointData.y);
              }}
              onMouseLeave={handlePointLeave}
              onClick={() => onCriterionClick && onCriterionClick(i)}
            >
              <div
                className={`w-3 h-3 rounded-full mr-2 transition-transform duration-200 ${isActive ? "scale-125" : ""}`}
                style={{ backgroundColor: getPointColor(score.percentage) }}
              />
              <span className="flex-1">{score.name}</span>
              <span className="ml-auto font-medium">{score.score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Sparkline component for showing progress over time
interface SparklineProps {
  data: number[];
  height?: number;
  width?: number;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  height = 30,
  width = 100,
}) => {
  // For simplicity, we're using placeholder data since we don't have historical data
  const placeholderData = [65, 59, 80, 81, 56, 55, 40, 70, 75, 77];
  const values = data.length > 0 ? data : placeholderData;

  // Calculate points for the sparkline
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1; // Avoid division by zero

  const getY = (value: number) => {
    // Invert Y axis since SVG 0,0 is top-left
    return height - ((value - min) / range) * (height - 5) - 2;
  };

  const getX = (index: number) => {
    return (index / (values.length - 1)) * width;
  };

  const points = values
    .map((value, index) => `${getX(index)},${getY(value)}`)
    .join(" ");

  return (
    <div className="sparkline inline-block">
      <svg width={width} height={height} className="overflow-visible">
        <polyline
          points={points}
          fill="none"
          stroke="#6366F1"
          strokeWidth="1.5"
          className="dark:stroke-indigo-400"
        />
        {/* Add a dot at the last point */}
        <circle
          cx={getX(values.length - 1)}
          cy={getY(values[values.length - 1])}
          r="2"
          fill="#6366F1"
          className="dark:fill-indigo-400"
        />
      </svg>
    </div>
  );
};

interface ChatMessageBoxProps {
  message: NewEssayEvaluationResult | undefined;
  t: (key: string) => string;
  onNavigate: () => void;
}

type TabType =
  | "summary"
  | "paragraphs"
  | "criteria"
  | "strengths"
  | "improvements";

export function ChatMessageBox({ message, onNavigate }: ChatMessageBoxProps) {
  const [activeTab, setActiveTab] = useState<TabType>("summary");
  const [expandedCriteria, setExpandedCriteria] = useState<number[]>([]);
  const [expandedParagraphs, setExpandedParagraphs] = useState<number[]>([]);
  const [showRadarChart, setShowRadarChart] = useState(false);
  const { t } = useTranslation();

  // Create a ref for each criterion
  const criteriaRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  if (!message) {
    return null;
  }

  const hasCriteria =
    message.criteria_evaluations !== undefined &&
    message.criteria_evaluations?.length > 0;

  const hasStrengths =
    hasCriteria &&
    message.criteria_evaluations?.some(
      (criterion) => criterion.strengths && criterion.strengths?.length > 0,
    );

  const hasImprovements =
    hasCriteria &&
    message.criteria_evaluations?.some(
      (criterion) =>
        criterion.areas_to_improve && criterion.areas_to_improve?.length > 0,
    );

  const hasParagraphs =
    message.paragraph_analysis !== undefined &&
    message.paragraph_analysis?.length > 0;

  const hasScore =
    message.score !== undefined && message.max_score !== undefined;

  const tabOptions = [
    {
      id: "summary" as TabType,
      label: t("assistant.essayGrader.overallSummary"),
      icon: <FileText className="h-4 w-4 mr-2" />,
      activeColor:
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
      inactiveColor:
        "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50",
      show: !!message.overall_summary,
    },
    {
      id: "paragraphs" as TabType,
      label: t("assistant.essayGrader.paragraphAnalysis"),
      icon: <Layers className="h-4 w-4 mr-2" />,
      activeColor:
        "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700",
      inactiveColor:
        "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50",
      show: hasParagraphs,
    },
    {
      id: "criteria" as TabType,
      label: t("assistant.essayGrader.criteriaEvaluations"),
      icon: <ClipboardList className="h-4 w-4 mr-2" />,
      activeColor:
        "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700",
      inactiveColor:
        "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50",
      show: hasCriteria,
    },
    {
      id: "strengths" as TabType,
      label: t("assistant.essayGrader.strengths"),
      icon: <CheckCircle className="h-4 w-4 mr-2" />,
      activeColor:
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700",
      inactiveColor:
        "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50",
      show: hasStrengths,
    },
    {
      id: "improvements" as TabType,
      label: t("assistant.essayGrader.areasToImprove"),
      icon: <AlertCircle className="h-4 w-4 mr-2" />,
      activeColor:
        "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700",
      inactiveColor:
        "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50",
      show: hasImprovements,
    },
  ];

  const visibleTabs = tabOptions.filter((tab) => tab.show);

  // PDF Document component
  const pdfDocument = <EssayGraderPdfDocument message={message} />;

  const toggleCriterion = (index: number) => {
    setExpandedCriteria((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleParagraph = (index: number) => {
    setExpandedParagraphs((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleAllCriteria = () => {
    if (message?.criteria_evaluations) {
      if (expandedCriteria.length === message.criteria_evaluations.length) {
        // If all are expanded, collapse all
        setExpandedCriteria([]);
      } else {
        // Otherwise, expand all
        setExpandedCriteria(
          message.criteria_evaluations.map((_, index) => index),
        );
      }
    }
  };

  const toggleAllParagraphs = () => {
    if (message?.paragraph_analysis) {
      if (expandedParagraphs.length === message.paragraph_analysis.length) {
        // If all are expanded, collapse all
        setExpandedParagraphs([]);
      } else {
        // Otherwise, expand all
        setExpandedParagraphs(
          message.paragraph_analysis.map((_, index) => index),
        );
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="mt-2 overflow-hidden"
    >
      <div className="flex flex-col max-h-[70vh] md:max-h-[80vh]">
        <div className="py-3 pl-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <Text variant="h3" className="text-xl font-medium">
              {t("assistant.essayGrader.aiAnalysis")}
            </Text>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {hasScore && (
                <div className="flex items-center bg-blue-50 dark:bg-blue-900/20 px-2 sm:px-3 py-2 rounded-xl border border-blue-300 dark:border-blue-700">
                  <Award className="h-4 w-4 text-blue-500 mr-1.5" />
                  <Text
                    variant="span"
                    className="font-medium text-blue-700 dark:text-blue-300 text-sm sm:text-base"
                  >
                    {t("assistant.essayGrader.totalScore")}: {message.score}/
                    {message.max_score}
                  </Text>
                </div>
              )}
              <PdfDownloadButton
                document={pdfDocument}
                btnTheme="bg-indigo-600 hover:bg-indigo-700"
                pdfTitle={t("assistant.essayGrader.pdfTitle")}
              />
            </div>
          </div>
        </div>

        {/* Tab Navigation as Chips in Grid */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 md:flex md:flex-wrap md:justify-center gap-2 min-w-max sm:min-w-0">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-center sm:justify-start px-2 sm:px-3 py-2 rounded-full text-xs sm:text-sm font-medium border transition-colors duration-200 ${
                  activeTab === tab.id ? tab.activeColor : tab.inactiveColor
                }`}
              >
                {tab.icon}
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div
          className="p-3 sm:p-6 overflow-y-auto"
          data-testid="essay-grader-message-content"
        >
          {/* Summary Tab */}
          {activeTab === "summary" && message.overall_summary && (
            <div className="space-y-4">
              <div className="p-3 sm:p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center mb-3">
                  <FileText className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                  <Text
                    variant="h4"
                    className="text-base sm:text-lg font-medium"
                  >
                    {t("assistant.essayGrader.overallSummary")}
                  </Text>
                </div>
                <div className="prose dark:prose-invert prose-blue max-w-none text-sm sm:text-base">
                  <Markdown>{message.overall_summary}</Markdown>
                </div>
              </div>

              {/* Add the radar chart to the summary */}
              {message.criteria_evaluations &&
                message.criteria_evaluations.length >= 3 && (
                  <div className="p-3 sm:p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <BarChart className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                        <Text
                          variant="h4"
                          className="text-base sm:text-lg font-medium"
                        >
                          {t("assistant.essayGrader.performanceChart")}
                        </Text>
                      </div>
                      {hasScore && (
                        <div className="flex items-center bg-blue-50 dark:bg-blue-900/20 px-2 sm:px-3 py-1.5 rounded-full">
                          <Award className="h-4 w-4 text-blue-500 mr-1.5" />
                          <Text
                            variant="span"
                            className="font-medium text-blue-700 dark:text-blue-300 text-sm sm:text-base"
                          >
                            {t("assistant.essayGrader.totalScore")}:{" "}
                            {message.score}/{message.max_score}
                          </Text>
                        </div>
                      )}
                    </div>

                    <RadarChart
                      criteria={message.criteria_evaluations.map((c) => ({
                        name: c.criterion_name || "",
                        score:
                          c.score !== undefined && c.max_score !== undefined
                            ? `${c.score}/${c.max_score}`
                            : "0/0",
                      }))}
                      onCriterionClick={(index) => {
                        setActiveTab("criteria");
                        setExpandedCriteria([index]);

                        // Add a small delay to ensure the state updates and DOM renders before scrolling
                        setTimeout(() => {
                          if (criteriaRefs.current[index]) {
                            criteriaRefs.current[index]?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }
                        }, 100);
                      }}
                    />
                  </div>
                )}
            </div>
          )}

          {/* Paragraph Analysis Tab */}
          {activeTab === "paragraphs" &&
            hasParagraphs &&
            message.paragraph_analysis && (
              <div className="space-y-4">
                <div className="flex justify-end mb-2">
                  <button
                    onClick={toggleAllParagraphs}
                    className="text-xs sm:text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 flex items-center transition-colors py-1"
                  >
                    {expandedParagraphs.length ===
                    message.paragraph_analysis.length ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        {t("assistant.essayGrader.collapseAll")}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        {t("assistant.essayGrader.expandAll")}
                      </>
                    )}
                  </button>
                </div>
                {message.paragraph_analysis.map((paragraph, index) => {
                  const isExpanded = expandedParagraphs.includes(index);
                  return (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-900 overflow-hidden"
                    >
                      {/* Accordion Header - Always Visible */}
                      <div
                        className="p-3 sm:p-4 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
                        onClick={() => toggleParagraph(index)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex items-center">
                            <Layers className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" />
                            <Text
                              variant="h4"
                              className="text-base sm:text-lg font-medium"
                            >
                              {t("assistant.essayGrader.paragraphNumber", {
                                number: paragraph.paragraph_number,
                              })}
                            </Text>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 ml-2 text-purple-500" />
                            ) : (
                              <ChevronDown className="h-4 w-4 ml-2 text-purple-500" />
                            )}
                          </div>
                          {paragraph.paragraph_type && (
                            <div className="inline-flex items-center px-3 py-1 bg-purple-50 dark:bg-purple-900/20 rounded-full self-start sm:self-auto">
                              <Text
                                variant="span"
                                className="text-xs sm:text-sm font-medium"
                              >
                                {t("assistant.essayGrader.paragraphType", {
                                  type: paragraph.paragraph_type,
                                })}
                              </Text>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Accordion Content - Visible when expanded */}
                      {isExpanded && (
                        <div className="p-3 sm:p-4 border-t border-purple-100 dark:border-purple-800">
                          {paragraph.content_and_structure_analysis && (
                            <div className="mb-4">
                              <Text
                                variant="h5"
                                className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
                              >
                                {t("assistant.essayGrader.contentAnalysis")}
                              </Text>
                              <div className="prose dark:prose-invert prose-sm max-w-none">
                                <Markdown remarkPlugins={[remarkGfm]}>
                                  {paragraph.content_and_structure_analysis}
                                </Markdown>
                              </div>
                            </div>
                          )}

                          {paragraph.key_quotes &&
                            paragraph.key_quotes.length > 0 && (
                              <div className="mt-4">
                                <Text
                                  variant="h5"
                                  className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
                                >
                                  {t("assistant.essayGrader.keyQuotes")}
                                </Text>
                                <div className="space-y-3">
                                  {paragraph.key_quotes.map(
                                    (quote, quoteIndex) => (
                                      <div
                                        key={quoteIndex}
                                        className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-600"
                                      >
                                        <div className="flex items-start gap-2">
                                          <Quote className="h-4 w-4 text-gray-500 dark:text-gray-400 mt-1 flex-shrink-0" />
                                          <div>
                                            <Text
                                              variant="p"
                                              className="text-sm font-medium text-gray-800 dark:text-gray-200 italic"
                                            >
                                              "{quote.quote}"
                                            </Text>
                                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 prose dark:prose-invert prose-xs max-w-none">
                                              <Markdown
                                                remarkPlugins={[remarkGfm]}
                                              >
                                                {quote.explanation}
                                              </Markdown>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          {/* Criteria Evaluations Tab */}
          {activeTab === "criteria" &&
            hasCriteria &&
            message.criteria_evaluations && (
              <div className="space-y-4">
                <div className="flex justify-end items-center gap-4 mb-2">
                  {message.criteria_evaluations.length >= 3 && (
                    <button
                      onClick={() => setShowRadarChart((prev) => !prev)}
                      className="text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center transition-colors py-1"
                    >
                      <BarChart className="h-3 w-3 mr-1" />
                      {showRadarChart
                        ? t("assistant.essayGrader.hidePerformanceChart")
                        : t("assistant.essayGrader.showPerformanceChart")}
                    </button>
                  )}

                  <button
                    onClick={toggleAllCriteria}
                    className="text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center transition-colors py-1"
                  >
                    {expandedCriteria.length ===
                    message.criteria_evaluations.length ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        {t("assistant.essayGrader.collapseAll")}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        {t("assistant.essayGrader.expandAll")}
                      </>
                    )}
                  </button>
                </div>

                {/* Radar chart visualization */}
                {showRadarChart && message.criteria_evaluations.length >= 3 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <RadarChart
                      criteria={message.criteria_evaluations.map((c) => ({
                        name: c.criterion_name || "",
                        score:
                          c.score !== undefined && c.max_score !== undefined
                            ? `${c.score}/${c.max_score}`
                            : "0/0",
                      }))}
                      onCriterionClick={(index) => {
                        setActiveTab("criteria");
                        setExpandedCriteria([index]);

                        // Add a small delay to ensure the state updates and DOM renders before scrolling
                        setTimeout(() => {
                          if (criteriaRefs.current[index]) {
                            criteriaRefs.current[index]?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                          }
                        }, 100);
                      }}
                    />
                  </motion.div>
                )}

                {message.criteria_evaluations.map((criterion, index) => {
                  const isExpanded = expandedCriteria.includes(index);
                  return (
                    <div
                      key={index}
                      ref={(el) => (criteriaRefs.current[index] = el)}
                      className="bg-white dark:bg-gray-800 rounded-lg border border-indigo-200 dark:border-indigo-900 overflow-hidden"
                    >
                      {/* Accordion Header - Always Visible */}
                      <div
                        className="p-3 sm:p-4 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors"
                        onClick={() => toggleCriterion(index)}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <ClipboardList className="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" />
                              <Text
                                variant="h4"
                                className="text-base sm:text-lg font-medium"
                              >
                                {t("assistant.essayGrader.criterionName", {
                                  name: criterion.criterion_name,
                                })}
                              </Text>
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 ml-2 text-indigo-500" />
                              ) : (
                                <ChevronDown className="h-4 w-4 ml-2 text-indigo-500" />
                              )}
                            </div>
                            {criterion.criterion_description && (
                              <Text
                                variant="p"
                                className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-7"
                              >
                                {criterion.criterion_description}
                              </Text>
                            )}
                          </div>
                          {criterion.score !== undefined &&
                            criterion.max_score !== undefined && (
                              <div className="inline-flex items-center gap-2">
                                <ScoreCircle
                                  score={`${criterion.score}/${criterion.max_score}`}
                                  size="sm"
                                  showText={false}
                                />
                                <div className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 rounded-full">
                                  <Text
                                    variant="span"
                                    className="text-xs sm:text-sm font-medium"
                                  >
                                    {t("assistant.essayGrader.criterionScore", {
                                      score: `${criterion.score}/${criterion.max_score}`,
                                    })}
                                  </Text>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Accordion Content - Visible when expanded */}
                      {isExpanded && (
                        <div className="p-3 sm:p-4 border-t border-indigo-100 dark:border-indigo-800">
                          {criterion.justification_with_evidence && (
                            <div className="mb-4">
                              <Text
                                variant="h5"
                                className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
                              >
                                {t("assistant.essayGrader.justification")}
                              </Text>
                              <div className="prose dark:prose-invert prose-sm max-w-none">
                                <Markdown remarkPlugins={[remarkGfm]}>
                                  {criterion.justification_with_evidence}
                                </Markdown>
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {criterion.strengths &&
                              criterion.strengths.length > 0 && (
                                <div className="space-y-2">
                                  <Text
                                    variant="h5"
                                    className="text-sm font-medium mb-1 text-green-600 dark:text-green-400 flex items-center"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                                    {t("assistant.essayGrader.strengths")}
                                  </Text>
                                  <ul className="space-y-1 list-disc pl-5">
                                    {criterion.strengths.map(
                                      (strength, strengthIndex) => (
                                        <li
                                          key={strengthIndex}
                                          className="text-xs sm:text-sm"
                                        >
                                          {strength}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}

                            {criterion.areas_to_improve &&
                              criterion.areas_to_improve.length > 0 && (
                                <div className="space-y-2">
                                  <Text
                                    variant="h5"
                                    className="text-sm font-medium mb-1 text-amber-600 dark:text-amber-400 flex items-center"
                                  >
                                    <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                                    {t("assistant.essayGrader.areasToImprove")}
                                  </Text>
                                  <ul className="space-y-1 list-disc pl-5">
                                    {criterion.areas_to_improve.map(
                                      (area, areaIndex) => (
                                        <li
                                          key={areaIndex}
                                          className="text-xs sm:text-sm"
                                        >
                                          {area}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          {/* Strengths Tab */}
          {activeTab === "strengths" &&
            hasStrengths &&
            message.criteria_evaluations && (
              <div className="p-3 sm:p-6 bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-900">
                <div className="flex items-center mb-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <Text
                    variant="h4"
                    className="text-base sm:text-lg font-medium"
                  >
                    {t("assistant.essayGrader.strengths")}
                  </Text>
                </div>
                <div className="space-y-4">
                  {message.criteria_evaluations.map((criterion, index) => {
                    if (
                      !criterion.strengths ||
                      criterion.strengths.length === 0
                    )
                      return null;

                    return (
                      <div
                        key={index}
                        className="border-t border-gray-100 dark:border-gray-700 pt-3 first:border-t-0 first:pt-0"
                      >
                        <Text
                          variant="h5"
                          className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
                        >
                          {criterion.criterion_name}
                        </Text>
                        <ul className="space-y-2 list-disc pl-5">
                          {criterion.strengths.map(
                            (strength, strengthIndex) => (
                              <li
                                key={strengthIndex}
                                className="text-xs sm:text-base"
                              >
                                {strength}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Areas to Improve Tab */}
          {activeTab === "improvements" &&
            hasImprovements &&
            message.criteria_evaluations && (
              <div className="p-3 sm:p-6 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-900">
                <div className="flex items-center mb-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                  <Text
                    variant="h4"
                    className="text-base sm:text-lg font-medium"
                  >
                    {t("assistant.essayGrader.areasToImprove")}
                  </Text>
                </div>
                <div className="space-y-4">
                  {message.criteria_evaluations.map((criterion, index) => {
                    if (
                      !criterion.areas_to_improve ||
                      criterion.areas_to_improve.length === 0
                    )
                      return null;

                    return (
                      <div
                        key={index}
                        className="border-t border-gray-100 dark:border-gray-700 pt-3 first:border-t-0 first:pt-0"
                      >
                        <Text
                          variant="h5"
                          className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
                        >
                          {criterion.criterion_name}
                        </Text>
                        <ul className="space-y-2 list-disc pl-5">
                          {criterion.areas_to_improve.map((area, areaIndex) => (
                            <li
                              key={areaIndex}
                              className="text-xs sm:text-base"
                            >
                              {area}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>
      </div>
    </motion.div>
  );
}
